<template>
  <div class="jsone">
    <div v-if="label" class="jsone__label">
      {{ label }}
      <span v-if="required" style="color: red;">*</span>
      <q-icon v-if="hint" name="help_outline" size="15px" class="jsone__help">
        <q-tooltip max-width="340px" anchor="top middle" self="bottom middle" :delay="100">
          <span v-html="hint"></span>
        </q-tooltip>
      </q-icon>
    </div>

    <div class="jsone__toolbar">
      <span
        class="jsone__status"
        :class="statusValid ? 'jsone__status--ok' : 'jsone__status--err'"
      >
        <q-icon :name="statusValid ? 'check_circle' : 'error'" size="13px" class="q-mr-xs" />
        {{ statusText }}
      </span>
      <q-space />
      <q-btn
        flat dense no-caps
        size="sm"
        color="primary"
        icon="auto_fix_high"
        label="Форматировать"
        :disable="!canFormat"
        @click="format"
      />
    </div>

    <div class="jsone__box" :class="{ 'jsone__box--err': !statusValid }">
      <pre class="jsone__highlight" ref="highlight" aria-hidden="true"><code v-html="highlightedHtml"></code></pre>
      <textarea
        ref="ta"
        class="jsone__textarea"
        :rows="rows"
        :placeholder="placeholder"
        spellcheck="false"
        :value="stringValue"
        @input="onInput"
        @scroll="syncScroll"
        @keydown.tab.prevent="onTab"
        @blur="onBlur"
      ></textarea>
    </div>
  </div>
</template>

<script>
function escapeHtml(str) {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export default {
  name: "JsonEditor",
  props: {
    value: { type: [String, Object], default: "" },
    label: { type: String, default: "" },
    hint: { type: String, default: "" },
    required: { type: Boolean, default: false },
    rows: { type: [Number, String], default: 6 },
    placeholder: { type: String, default: '{\n  "key": "value"\n}' },
  },
  computed: {
    stringValue() {
      const v = this.value;
      if (v == null) return "";
      if (typeof v === "string") return v;
      try {
        return JSON.stringify(v, null, 2);
      } catch (e) {
        return String(v);
      }
    },
    isEmpty() {
      return !this.stringValue || !this.stringValue.trim();
    },
    parseError() {
      if (this.isEmpty) return null;
      try {
        JSON.parse(this.stringValue);
        return null;
      } catch (e) {
        return e.message;
      }
    },
    statusValid() {
      return !this.parseError;
    },
    canFormat() {
      return !this.isEmpty && this.statusValid;
    },
    statusText() {
      if (this.isEmpty) return "Пусто";
      if (this.statusValid) return "Корректный JSON";
      return `Ошибка: ${this.parseError}`;
    },
    highlightedHtml() {
      const raw = this.stringValue || "";
      const esc = escapeHtml(raw);
      const html = esc.replace(
        /("(?:\\u[a-zA-Z0-9]{4}|\\[^u]|[^\\"])*"(?:\s*:)?|\b(?:true|false)\b|\bnull\b|-?\d+(?:\.\d*)?(?:[eE][+-]?\d+)?)/g,
        (match) => {
          let cls = "jsone__t-num";
          if (/^&quot;|^"/.test(match)) {
            cls = /:\s*$/.test(match) ? "jsone__t-key" : "jsone__t-str";
          } else if (/^(true|false)$/.test(match)) {
            cls = "jsone__t-bool";
          } else if (match === "null") {
            cls = "jsone__t-null";
          }
          return `<span class="${cls}">${match}</span>`;
        }
      );
      return html + "\n";
    },
  },
  methods: {
    onInput(e) {
      this.$emit("change_input", e.target.value);
      this.$nextTick(this.syncScroll);
    },
    syncScroll() {
      const ta = this.$refs.ta;
      const hl = this.$refs.highlight;
      if (!ta || !hl) return;
      hl.scrollTop = ta.scrollTop;
      hl.scrollLeft = ta.scrollLeft;
    },
    onTab(e) {
      const ta = e.target;
      const start = ta.selectionStart;
      const end = ta.selectionEnd;
      const indent = "  ";
      const next = ta.value.slice(0, start) + indent + ta.value.slice(end);
      this.$emit("change_input", next);
      this.$nextTick(() => {
        ta.selectionStart = ta.selectionEnd = start + indent.length;
      });
    },
    onBlur() {
      if (this.canFormat) this.format();
    },
    format() {
      if (!this.canFormat) return;
      try {
        const pretty = JSON.stringify(JSON.parse(this.stringValue), null, 2);
        this.$emit("change_input", pretty);
      } catch (e) {
        /* ignore */
      }
    },
  },
  mounted() {
    if (this.value != null && typeof this.value !== "string") {
      this.$emit("change_input", this.stringValue);
    }
  },
};
</script>

<style scoped lang="scss">
.jsone {
  $font: "SFMono-Regular", "Menlo", "Consolas", "Liberation Mono", monospace;
  $fs: 12px;
  $lh: 1.5;
  $pad: 10px;

  &__label {
    font-size: 13px;
    line-height: 13px;
    margin-bottom: 8px;
  }

  &__help {
    color: #94a3b8;
    cursor: help;
    margin-left: 4px;
    vertical-align: middle;

    &:hover {
      color: #2563eb;
    }
  }

  &__toolbar {
    display: flex;
    align-items: center;
    margin-bottom: 4px;
  }

  &__status {
    display: inline-flex;
    align-items: center;
    font-size: 11px;
    font-weight: 500;

    &--ok {
      color: #16a34a;
    }
    &--err {
      color: #dc2626;
    }
  }

  &__box {
    position: relative;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    overflow: hidden;
    background: #fbfdff;

    &:focus-within {
      border-color: #3b82f6;
      box-shadow: 0 0 0 1px rgba(59, 130, 246, 0.25);
    }

    &--err {
      border-color: #fca5a5;
    }
  }

  &__highlight,
  &__textarea {
    margin: 0;
    padding: $pad;
    font-family: $font;
    font-size: $fs;
    line-height: $lh;
    white-space: pre-wrap;
    word-break: break-word;
    overflow-wrap: break-word;
    tab-size: 2;
    border: 0;
    box-sizing: border-box;
  }

  &__highlight {
    position: absolute;
    inset: 0;
    margin: 0;
    pointer-events: none;
    overflow: auto;
    color: #1e293b;

    code {
      font-family: inherit;
      white-space: inherit;
      word-break: inherit;
    }
  }

  &__textarea {
    position: relative;
    display: block;
    width: 100%;
    min-height: calc(#{$fs} * #{$lh} * 6);
    resize: vertical;
    background: transparent;
    color: transparent;
    caret-color: #1e293b;
    outline: none;

    &::placeholder {
      color: #9ca3af;
      -webkit-text-fill-color: #9ca3af;
    }
  }

  /* Подсветка токенов */
  :deep(.jsone__t-key) {
    color: #7c3aed;
    font-weight: 600;
  }
  :deep(.jsone__t-str) {
    color: #15803d;
  }
  :deep(.jsone__t-num) {
    color: #2563eb;
  }
  :deep(.jsone__t-bool) {
    color: #c2410c;
    font-weight: 600;
  }
  :deep(.jsone__t-null) {
    color: #9333ea;
    font-style: italic;
  }
}
</style>
